import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe, DollarSign, Moon, Sun, MessageCircle, Send, Music2, Facebook, Twitter, Rss, Instagram, Youtube, Phone } from "lucide-react";
import { getFooter } from "../api";
import { useAuth } from "../context/AuthContext";
import { getCcy, setCcy, ccyLabel } from "../currency";
import "../footer2.css";

const ICONS = {
  discord: MessageCircle, telegram: Send, tiktok: Music2, facebook: Facebook,
  "x-twitter": Twitter, reddit: Rss, instagram: Instagram, youtube: Youtube, whatsapp: Phone,
};
const LANGS = { en: "English", ar: "العربية", hi: "हिन्दी", de: "Deutsch" };

const isInternal = (h) => h && h.startsWith("/");

export default function SiteFooter() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [dark, setDark] = useState(() => localStorage.getItem("imperio_theme") !== "light");
  const [ccy, setCcyState] = useState(getCcy());

  useEffect(() => { getFooter().then(setCfg).catch(() => setCfg(null)); }, []);

  const toggleTheme = () => {
    const nv = !dark; setDark(nv); localStorage.setItem("imperio_theme", nv ? "dark" : "light");
    window.location.reload();
  };
  const cycleCcy = () => {
    const list = ["USD-$", "EUR-€", "GBP-£", "INR-₹", "AED-د.إ"];
    const next = list[(list.indexOf(ccy) + 1 + list.length) % list.length] || "USD-$";
    setCcy(next); setCcyState(next);
  };
  const cycleLang = () => {
    const codes = ["en", "ar", "hi", "de"];
    const next = codes[(codes.indexOf(i18n.language) + 1) % codes.length];
    i18n.changeLanguage(next); localStorage.setItem("imperio_lang", LANGS[next]);
  };

  if (!cfg) return null;
  // Hide the marketing footer once the client is logged in.
  if (user) return null;

  const hasHref = (h) => h && h !== "#";
  const Item = ({ l }) => isInternal(l.href)
    ? <Link to={l.href} className="sf-link">{l.label}</Link>
    : <a href={l.href || "#"} className="sf-link" {...(l.href && l.href !== "#" ? { target: "_blank", rel: "noreferrer" } : {})}>{l.label}</a>;

  return (
    <footer className="sf-root" data-testid="site-footer">
      <div className="sf-inner">
        <div className="sf-community">
          <h4>{cfg.community_title || "Community"}</h4>
          <div className="sf-socials" data-testid="sf-socials">
            {(cfg.socials || []).filter((s) => hasHref(s.href)).map((s, i) => {
              const Ic = ICONS[s.icon] || Globe;
              return (
                <a key={i} href={s.href} className="sf-social" aria-label={s.icon} target="_blank" rel="noreferrer">
                  <Ic size={20} />
                </a>
              );
            })}
          </div>
          <button className="sf-pref" onClick={cycleLang} data-testid="sf-lang"><Globe size={18} /> {LANGS[i18n.language] || "English"}</button>
          <button className="sf-pref" onClick={cycleCcy} data-testid="sf-ccy"><DollarSign size={18} /> {ccyLabel(ccy)}</button>
          <div className="sf-theme"><span>Theme</span>
            <button className={`sf-toggle${dark ? " on" : ""}`} onClick={toggleTheme} data-testid="sf-theme">
              <span className="sf-knob">{dark ? <Moon size={11} /> : <Sun size={11} />}</span>
            </button>
          </div>
        </div>

        <div className="sf-cols">
          {(cfg.columns || []).map((col, i) => (
            <div className="sf-col" key={i} data-testid={`sf-col-${i}`}>
              <h4>{col.title}</h4>
              {(col.links || []).map((l, j) => <Item key={j} l={l} />)}
            </div>
          ))}
        </div>
      </div>
      <div className="sf-bottom">{cfg.copyright || "Imperio © 2026"}</div>
    </footer>
  );
}
